/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-floating-promises */
import { OnChangeFn, SortingState } from "@tanstack/react-table";
import { subDays } from "date-fns";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useUserPageParams from "../../../../../hooks/useUserPageParams";
import uuid from "react-uuid";
import useLocalStorageState from "use-local-storage-state";
import { getEntityActivity } from "../../../../../services/userActivityEndpoint";
import { LegacyColumn } from "../../../../../shared/table/legacyTanStackAdapter";
import TanStackTable from "../../../../../shared/table/TanStackTable";
import dateConverter from "../../../../../utils/dateConverter";

const UsersTable = ({ metric, height, workspaceId }: any) => {
  const params: any = useUserPageParams();
  const [tableColumns] = useLocalStorageState<LegacyColumn[]>("he-entity-activity-prefs", {
    defaultValue: [
      {
        key: "event",
        dataType: "object",
        title: "Action Performed",
        colGroup: { style: { width: 280 } },
        isSortable: false,
      }, {
        key: "_timestamp",
        dataType: "string",
        title: "Time",
        colGroup: { style: { width: 200 } },
        isSortable: true,
      }, {
        key: "src",
        dataType: "string",
        title: "Source",
        colGroup: { style: { width: 160 } },
        isSortable: true,
      }, {
        key: "url",
        title: "Page",
        colGroup: { style: { width: 340 } },
        isSortable: false,
      },
    ],
  });

  const tableHeight = useMemo(() => window.innerHeight - 535, [height]);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const pageSize = 20;
  const [total, setTotal] = useState<number>(0);
  const [sorting, setSorting] = useState<SortingState>([]);
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total],
  );

  const calcDate: any = useMemo(() => {
    if (metric?.name) {
      const val = parseInt(metric.name.match(/[0-9]+/) ? metric.name.match(/[0-9]+/)[0] : "1", 10);
      return subDays(new Date(), val);
    }
    return new Date("1-1-1970");
  }, [metric?.name]);

  useEffect(() => {
    setPageIndex(0);
    setSorting([]);
  }, [metric?.id]);

  const onTableSortingChange: OnChangeFn<SortingState> = useCallback((updater) => {
    const nextSorting = typeof updater === "function" ? updater(sorting) : updater;
    setSorting((nextSorting || []).slice(0, 1));
  }, [sorting]);

  const fetchData = useCallback(async (page: number) => {
    if (!workspaceId || !params?.userId) {
      setRows([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    try {
      const response = await getEntityActivity({
        external_id: params.userId,
        workspace_identifier: workspaceId,
        entity_type: "contacts",
        start_time: dateConverter(calcDate),
        offset: page * pageSize,
        limit: pageSize,
      });

      if (response?.data) {
        const temp = response?.data?.results.map((item: any) => {
          const value = { ...item };
          if (value?.event) {
            value.event = {
              event: value?.event,
              category: value?.event_category,
              ...value.traits,
            };
            delete (value.category);
            delete (value?.traits);
          }
          value.randomId = uuid();
          return value;
        });

        const totalHits = response.data.total_hits || 0;
        setRows(temp);
        setTotal(totalHits);
        if (totalHits <= (page * pageSize) && page > 0) {
          setPageIndex(0);
        }
      } else {
        setRows([]);
        setTotal(0);
        setPageIndex(0);
      }
    } catch (error) {
      setRows([]);
      setTotal(0);
      setPageIndex(0);
    } finally {
      setLoading(false);
    }
  }, [
    params?.userId,
    workspaceId,
    calcDate,
    pageSize,
  ]);

  useEffect(() => {
    fetchData(pageIndex);
  }, [pageIndex, fetchData]);

  return (
    <TanStackTable
      legacyColumns={tableColumns || []}
      data={rows}
      totalData={total}
      pageIndex={pageIndex}
      pageSize={pageSize}
      pageCount={pageCount}
      onPageIndexChange={setPageIndex}
      sorting={sorting}
      onSortingChange={onTableSortingChange}
      loading={loading}
      noDataTitle="No Recent Activity"
      noDataMessage="No recent activity found for this user in the given time frame."
      itemType="Actions"
      height={tableHeight}
      surfaceVariant="lovable"
      enableColumnPinning={false}
    />
  );
};

export default UsersTable;
